import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { leadsApi, usersApi, customersApi } from '@/lib/api';
import { Plus, Search, Trash2, UserPlus } from 'lucide-react';
import {
  Button,
  Combobox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  Input,
  Pagination,
  Status,
  formatDatum,
  type Column,
  type DataTableSort,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { usePersistedFilters } from '@/hooks/usePersistedFilters';
import { StatusDropdown } from '@/features/customers/components/StatusDropdown';
import { ConvertLeadModal } from './ConvertLeadModal';
import { LeadSheet } from './LeadForm';
import { acquisitionSourceLabels, type Customer } from '@/types';

const PER_PAGE = 50;

const sourceLabels: Record<string, string> = acquisitionSourceLabels;

const defaultFilters = {
  search: '',
  acquisition_source: '',
  assigned_to: '',
  sort_by: 'created_at',
  sort_order: 'desc' as 'asc' | 'desc',
};

export function LeadsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, updateFilters, resetFilters] = usePersistedFilters('leads', defaultFilters);
  const [page, setPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  // Der Weg zum Kunden laeuft auch aus der Liste ueber den Umwandeln-Dialog
  // (POST /leads/{id}/convert zieht die K-Nummer); ein Dialog fuer die ganze
  // Seite, nicht einer je Zeile.
  const [umzuwandeln, setUmzuwandeln] = useState<Customer | null>(null);

  // Bestaetigung als In-App-Dialog statt window.confirm (Trockenlauf 12.10.2026, Befund 9).
  // Der Dialog wartet auf das Loeschen und zeigt einen Fehler, statt still zu schliessen.
  const [zuLoeschen, setZuLoeschen] = useState<Customer | null>(null);
  const handleDelete = (e: React.MouseEvent, lead: Customer) => {
    e.stopPropagation();
    setZuLoeschen(lead);
  };
  const loeschenBestaetigt = async () => {
    if (!zuLoeschen) return;
    await customersApi.delete(zuLoeschen.id);
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['leads', { page, per_page: PER_PAGE, ...filters }],
    queryFn: () => leadsApi.list({
      page,
      per_page: PER_PAGE,
      search: filters.search || undefined,
      acquisition_source: filters.acquisition_source || undefined,
      assigned_to: filters.assigned_to || undefined,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    }),
  });

  const sortieren = ({ key, direction }: DataTableSort) => {
    updateFilters({ sort_by: key, sort_order: direction });
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.acquisition_source || filters.assigned_to;

  // Am Handy eine Zeile je Lead: Firma, darunter Status, Quelle und Ansprechpartner.
  const columns: Column<Customer>[] = [
    {
      key: 'company_name',
      header: 'Firma',
      sortable: true,
      mobil: 'titel',
      renderMobil: (lead) => lead.company_name,
      render: (lead) => (
        <div>
          <div className="font-medium">{lead.company_name}</div>
          <div className="t-mono text-muted-foreground">{lead.customer_number}</div>
        </div>
      ),
    },
    {
      key: 'acquisition_source',
      header: 'Quelle',
      sortable: true,
      mobil: 'unter',
      render: (lead) => sourceLabels[lead.acquisition_source || ''] || lead.acquisition_source || '–',
    },
    {
      key: 'primary_contact',
      header: 'Ansprechpartner',
      mobil: 'unter',
      renderMobil: (lead) => lead.primary_contact_name || '–',
      render: (lead) => lead.primary_contact_name || <span className="text-muted-foreground">–</span>,
    },
    {
      key: 'assigned_user_name',
      header: 'Zuständig',
      render: (lead) => lead.assigned_user_name || '–',
    },
    {
      key: 'last_interaction_at',
      header: 'Letzte Interaktion',
      sortable: true,
      mono: true,
      render: (lead) => formatDatum(lead.last_interaction_at),
    },
    {
      key: 'created_at',
      header: 'Angelegt',
      sortable: true,
      mono: true,
      render: (lead) => formatDatum(lead.created_at),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobil: 'status',
      // Am Handy oeffnet die Zeile den Lead; der Status wechselt dort.
      renderMobil: (lead) => <Status status={lead.status} variant="plain" />,
      // Bis zum 29.10.2026 stand hier dieselbe Pille wie am Handy, also nur
      // Optik. Wer die Geste aus der Kundenliste kannte, klickte darauf und
      // bekam nichts — keine Meldung, keinen Fehler (Henri, 29.10.2026).
      render: (lead) => (
        <StatusDropdown
          customer={lead}
          weiter={{ label: 'Als Kunde anlegen', onSelect: () => setUmzuwandeln(lead) }}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (lead) => (
        <IconButton icon={Trash2} label="Lead löschen" variant="ghost" size="sm" onClick={(e) => handleDelete(e, lead)} />
      ),
    },
  ];

  const totalPages = data?.pages ?? 0;

  return (
    <div>
      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Lead löschen?"
        description={zuLoeschen ? `„${zuLoeschen.company_name}“ wird endgültig gelöscht.` : ''}
        confirmLabel="Lead löschen"
        tone="danger"
        onConfirm={loeschenBestaetigt}
      />

      <CrmPageHeader
        title="Leads"
        subtitle={`${data?.total ?? 0} Leads insgesamt`}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setIsFormOpen(true)}>
            Lead anlegen
          </Button>
        }
      >
        <div className="flex w-full flex-wrap items-center gap-3 pb-4">
          <Input
            icon={Search}
            placeholder="Suchen"
            aria-label="Leads suchen"
            value={filters.search}
            onChange={(e) => { updateFilters({ search: e.target.value }); setPage(1); }}
            className="min-w-[200px] flex-1"
          />
          <Combobox
            className="w-48"
            aria-label="Alle Quellen"
            value={filters.acquisition_source}
            onChange={(v) => { updateFilters({ acquisition_source: v }); setPage(1); }}
            options={Object.entries(sourceLabels).map(([value, label]) => ({ value, label }))}
            placeholder="Alle Quellen"
          />
          <Combobox
            className="w-56"
            aria-label="Alle Mitarbeiter"
            value={filters.assigned_to}
            onChange={(v) => { updateFilters({ assigned_to: v }); setPage(1); }}
            options={users.map((u) => ({ value: u.id, label: u.name }))}
            placeholder="Alle Mitarbeiter"
          />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => { resetFilters(); setPage(1); }}>
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </CrmPageHeader>

      <DataTable<Customer>
        label="Leads"
        rows={data?.items ?? []}
        columns={columns}
        loading={isLoading}
        onRowClick={(lead) => navigate(`/leads/${lead.id}`)}
        empty={<EmptyState icon={UserPlus} title="Keine Leads gefunden" className="py-8" />}
        sort={{ key: filters.sort_by, direction: filters.sort_order }}
        onSortChange={sortieren}
      />

      {data && data.total > 0 && <Pagination page={page} pageCount={totalPages} onPageChange={setPage} />}

      <LeadSheet open={isFormOpen} onOpenChange={setIsFormOpen} />

      {umzuwandeln && (
        <ConvertLeadModal
          isOpen
          onClose={() => setUmzuwandeln(null)}
          leadId={umzuwandeln.id}
          leadName={umzuwandeln.company_name}
          leadNummer={umzuwandeln.customer_number}
          onConverted={(customerId) => {
            setUmzuwandeln(null);
            navigate(`/customers/${customerId}`);
          }}
        />
      )}
    </div>
  );
}
